/* eslint-disable security/detect-object-injection */
import React from 'react';
import {
  BucketInfo,
  BucketVisualState,
  bucketVisualState,
  BUCKET_LABELS,
  computeBuckets,
  StatusResponse,
} from './bucket-model';

export interface PrefixCardProps {
  prefix: string;
  status: StatusResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onBucketClick: (bucket: BucketInfo) => void;
  onRefresh: () => void;
  onRemove: () => void;
}

export const PrefixCard = (props: PrefixCardProps) => {
  const {
    prefix,
    status,
    isLoading,
    isError,
    onBucketClick,
    onRefresh,
    onRemove,
  } = props;

  const buckets = status ? computeBuckets(status) : [];
  const actionableBuckets = buckets.filter(
    b => b.action !== null && b.eligibleCount > 0
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <code className="text-base font-mono font-semibold text-gray-900">
              {prefix}
            </code>
            {status && (
              <span className="text-sm text-gray-500">
                Total: <b>{status.total}</b>
              </span>
            )}
          </div>
          <ActionBanner
            buckets={actionableBuckets}
            isLoading={isLoading}
            isError={isError}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="text-xs text-blue-600 hover:text-blue-800"
            onClick={onRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            className="text-xs text-red-600 hover:text-red-800"
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      </div>

      {isError && (
        <div className="text-sm text-red-700">Failed to load status.</div>
      )}

      {status && (
        <div className="grid grid-cols-9 gap-2">
          {buckets.map(bucket => (
            <BucketPill
              key={bucket.key}
              bucket={bucket}
              onClick={() => onBucketClick(bucket)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ActionBanner = ({
  buckets,
  isLoading,
  isError,
}: {
  buckets: BucketInfo[];
  isLoading: boolean;
  isError: boolean;
}) => {
  if (isError) return null;
  if (isLoading && buckets.length === 0) {
    return <div className="text-xs text-gray-500 mt-0.5">Loading status…</div>;
  }
  if (buckets.length === 0) {
    return (
      <div className="text-xs text-gray-500 mt-0.5">
        no action — awaiting pipeline
      </div>
    );
  }
  const summary = buckets
    .map(b => `${b.eligibleCount} at "${BUCKET_LABELS[b.key]?.short ?? b.key}"`)
    .join(' · ');
  return (
    <div className="text-xs text-green-700 font-medium mt-0.5">
      ⚡ {buckets.length} action{buckets.length === 1 ? '' : 's'} available —{' '}
      {summary}
    </div>
  );
};

const STATE_STYLES: Record<
  BucketVisualState,
  {bg: string; border: string; text: string; count: string}
> = {
  empty: {
    bg: 'bg-gray-50',
    border: 'border-gray-100',
    text: 'text-gray-400',
    count: 'text-gray-300',
  },
  waiting: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-900',
    count: 'text-blue-800',
  },
  action: {
    bg: 'bg-green-50',
    border: 'border-green-500',
    text: 'text-green-900',
    count: 'text-green-800',
  },
  'partial-review': {
    bg: 'bg-gradient-to-br from-green-50 to-amber-50',
    border: 'border-amber-500',
    text: 'text-amber-900',
    count: 'text-amber-800',
  },
  'terminal-success': {
    bg: 'bg-yellow-50',
    border: 'border-yellow-400',
    text: 'text-yellow-900',
    count: 'text-yellow-800',
  },
  'terminal-canceled': {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-900',
    count: 'text-red-800',
  },
};

const BucketPill = ({
  bucket,
  onClick,
}: {
  bucket: BucketInfo;
  onClick: () => void;
}) => {
  const state = bucketVisualState(bucket);
  const s = STATE_STYLES[state];
  const showActionChip = state === 'action' || state === 'partial-review';
  const borderWidth =
    state === 'action' || state === 'partial-review' ? 'border-2' : 'border';

  const tooltip = buildTooltip(bucket);

  return (
    <button
      onClick={onClick}
      disabled={bucket.total === 0}
      title={tooltip}
      className={`relative ${s.bg} ${
        s.border
      } ${borderWidth} rounded-md p-2 text-left transition-all ${
        bucket.total === 0
          ? 'cursor-default'
          : 'hover:shadow-md cursor-pointer hover:-translate-y-0.5'
      }`}
    >
      {showActionChip && (
        <span className="absolute -top-1.5 -right-1.5 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
          ⚡
        </span>
      )}
      <div
        className={`text-[10px] uppercase tracking-wide ${s.text} opacity-70`}
      >
        {bucket.index}
      </div>
      <div className={`text-xs ${s.text} truncate`}>{bucket.shortLabel}</div>
      <div className={`text-xl font-bold ${s.count}`}>{bucket.total}</div>
      {showActionChip && (
        <div className="text-[10px] font-semibold text-green-800 mt-0.5">
          {bucket.eligibleCount} ready
        </div>
      )}
    </button>
  );
};

function buildTooltip(bucket: BucketInfo): string {
  const parts: string[] = [`Bucket ${bucket.index}: ${bucket.label}`];
  parts.push(`Total: ${bucket.total}`);
  if (bucket.eligibleCount > 0 && bucket.action) {
    parts.push(`Eligible for ${bucket.action}: ${bucket.eligibleCount}`);
  }
  if (bucket.phiDetectedCount > 0) {
    parts.push(`PHI detected (human review): ${bucket.phiDetectedCount}`);
  }
  if (bucket.inProgressCount > 0 && bucket.action) {
    parts.push(`In progress: ${bucket.inProgressCount}`);
  }
  return parts.join('\n');
}
